import { createSignal, createMemo, For, Show, Suspense } from "solid-js";
import { useSearchParams, A, createAsync, useAction, type RouteDefinition } from "@solidjs/router";
import { AppShell, PageHeader } from "~/components/layout";
import {
  Button, Input, Badge, Card, CardContent, CardHeader, CardTitle, Skeleton, EmptyState,
  Tabs, TabsList, TabsTrigger, TabsContent, ScrollArea
} from "~/components/ui";
import { 
  searchVideos, searchChunks, semanticSearchVideos, getChannels, getTopics, 
  getSavedSearches, saveSearch, deleteSavedSearch, updateSearchAlert,
  type SemanticSearchResult, type SavedSearch 
} from "~/server/bff";
import { formatRelativeTime, formatDuration } from "~/lib/utils";
import {
  Search, FileText, Video, Filter, Star, Plus, FolderPlus, Bell, BellOff, Trash2, Play as PlayIcon, Sparkles, Zap, Loader2
} from "lucide-solid";

export const route = {
  preload: () => Promise.all([getChannels(), getTopics(), getSavedSearches()]),
} satisfies RouteDefinition;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [queryStr, setQueryStr] = createSignal((searchParams.q as string) || "");
  const [activeQuery, setActiveQuery] = createSignal((searchParams.q as string) || "");
  const [mode, setMode] = createSignal<"videos" | "chunks" | "semantic">(
    (searchParams.mode as "videos" | "chunks" | "semantic") || "videos"
  );
  const [showFilters, setShowFilters] = createSignal(false);
  const [showSavedSearches, setShowSavedSearches] = createSignal(true);
  const [isSaving, setIsSaving] = createSignal(false);
  const [actionLoading, setActionLoading] = createSignal<string | null>(null);

  const channels = createAsync(() => getChannels(), { deferStream: true });
  const topics = createAsync(() => getTopics(), { deferStream: true });
  const savedSearches = createAsync(() => getSavedSearches(), { deferStream: true });

  const saveSearchAction = useAction(saveSearch);
  const deleteSearchAction = useAction(deleteSavedSearch);
  const updateAlertAction = useAction(updateSearchAlert);

  // Search results - only fetch when activeQuery changes
  const videoResults = createAsync(() =>
    activeQuery() && mode() === "videos" ? searchVideos(activeQuery()) : Promise.resolve([]),
    { deferStream: true }
  );
  const chunkResults = createAsync(() =>
    activeQuery() && mode() === "chunks" ? searchChunks(activeQuery()) : Promise.resolve([]),
    { deferStream: true }
  );
  // Semantic search (vector/embedding-based)
  const semanticResults = createAsync(() =>
    activeQuery() && mode() === "semantic" ? semanticSearchVideos(activeQuery()) : Promise.resolve([]),
    { deferStream: true }
  );

  const searchResults = createMemo(() => {
    if (mode() === "semantic") return semanticResults();
    return mode() === "videos" ? videoResults() : chunkResults();
  });

  const handleSearch = (e: Event) => {
    e.preventDefault();
    setActiveQuery(queryStr());
    setSearchParams({ q: queryStr(), mode: mode() });
  };

  const handleRunSavedSearch = (search: SavedSearch) => {
    setQueryStr(search.query);
    setActiveQuery(search.query);
    setSearchParams({ q: search.query, mode: mode() });
  };

  const toggleSearchAlerts = async (id: string, currentState: boolean) => {
    setActionLoading(`alert-${id}`);
    try {
      const formData = new FormData();
      formData.append("searchId", id);
      formData.append("alertsEnabled", (!currentState).toString());
      await updateAlertAction(formData);
    } catch (error) {
      console.error("Failed to toggle alerts:", error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteSearch = async (id: string) => {
    if (!confirm("Delete this saved search?")) return;
    setActionLoading(`delete-${id}`);
    try {
      const formData = new FormData();
      formData.append("searchId", id);
      await deleteSearchAction(formData);
    } catch (error) {
      console.error("Failed to delete search:", error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveCurrentSearch = async () => {
    if (!queryStr().trim()) return;
    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append("name", queryStr());
      formData.append("query", queryStr());
      formData.append("filters", JSON.stringify({}));
      formData.append("alertsEnabled", "false");
      await saveSearchAction(formData);
    } catch (error) {
      console.error("Failed to save search:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AppShell title="Search">
      <div class="flex h-[calc(100vh-4rem)]">
        <Show when={showSavedSearches()}>
          <div class="w-72 border-r flex flex-col bg-muted/30">
            <div class="p-4 border-b">
              <h2 class="font-semibold">Saved Searches</h2>
              <p class="text-xs text-muted-foreground mt-1">
                Quick access to your saved queries
              </p>
            </div>
            <ScrollArea class="flex-1 p-2">
              <Suspense fallback={<SavedSearchesSkeleton />}>
                <Show when={savedSearches()} fallback={<SavedSearchesSkeleton />}>
                  {(items) => (
                    <div class="space-y-1">
                      <Show
                        when={items().length > 0}
                        fallback={
                          <p class="text-xs text-muted-foreground p-2">
                            No saved searches yet. Save a search to quickly run it again.
                          </p>
                        }
                      >
                        <For each={items()}>
                          {(search) => (
                            <div class="group p-2 rounded-md hover:bg-accent">
                              <div class="flex items-start justify-between">
                                <button
                                  class="flex-1 text-left"
                                  onClick={() => handleRunSavedSearch(search)}
                                >
                                  <p class="text-sm font-medium">{search.name}</p>
                                  <p class="text-xs text-muted-foreground font-mono mt-0.5">
                                    {search.query}
                                  </p>
                                </button>
                                <div class="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={() => toggleSearchAlerts(search.id, search.alertsEnabled)}
                                    disabled={actionLoading() === `alert-${search.id}`}
                                    title={search.alertsEnabled ? "Disable alerts" : "Enable alerts"}
                                    aria-label={search.alertsEnabled ? "Disable alerts" : "Enable alerts"}
                                  >
                                    <Show when={actionLoading() === `alert-${search.id}`} fallback={
                                      <Show when={search.alertsEnabled} fallback={<BellOff class="h-3 w-3" />}>
                                        <Bell class="h-3 w-3 text-primary" />
                                      </Show>
                                    }>
                                      <Loader2 class="h-3 w-3 animate-spin" />
                                    </Show>
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={() => handleDeleteSearch(search.id)}
                                    disabled={actionLoading() === `delete-${search.id}`}
                                    title="Delete"
                                    aria-label="Delete saved search"
                                  >
                                    <Show when={actionLoading() === `delete-${search.id}`} fallback={
                                      <Trash2 class="h-3 w-3" />
                                    }>
                                      <Loader2 class="h-3 w-3 animate-spin" />
                                    </Show>
                                  </Button>
                                </div>
                              </div>
                              <Show when={search.alertsEnabled}>
                                <Badge variant="secondary" class="text-[10px] mt-1">
                                  Alerts on
                                </Badge>
                              </Show>
                              <Show when={search.lastRun}>
                                {(lastRun) => (
                                  <p class="text-[10px] text-muted-foreground mt-1">
                                    Last run: {formatRelativeTime(lastRun())}
                                  </p>
                                )}
                              </Show>
                            </div>
                          )}
                        </For>
                      </Show>
                    </div>
                  )}
                </Show>
              </Suspense>
            </ScrollArea>
          </div>
        </Show>

        <div class="flex-1 p-6 overflow-auto">
          <PageHeader
            title="Search"
            description="Find videos, transcripts, and insights"
            actions={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSavedSearches(!showSavedSearches())}
              >
                {showSavedSearches() ? "Hide" : "Show"} Saved
              </Button>
            }
          />

        <div class="space-y-4">
          <form onSubmit={handleSearch} class="flex gap-2">
            <div class="relative flex-1">
              <Search class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search across all content..."
                class="pl-9"
                value={queryStr()}
                onInput={(e) => setQueryStr(e.currentTarget.value)}
              />
            </div>
            <Button type="submit">Search</Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleSaveCurrentSearch}
              disabled={!queryStr().trim() || isSaving()}
              title="Save this search"
              aria-label="Save this search"
            >
              <Show when={isSaving()} fallback={<Star class="h-4 w-4" />}>
                <Loader2 class="h-4 w-4 animate-spin" />
              </Show>
            </Button>
            <Button
              type="button"
              variant={showFilters() ? "secondary" : "outline"}
              onClick={() => setShowFilters(!showFilters())}
              aria-label={showFilters() ? "Hide filters" : "Show filters"}
              title={showFilters() ? "Hide filters" : "Show filters"}
            >
              <Filter class="h-4 w-4" />
            </Button>
          </form>

          <Tabs value={mode()} onChange={(v) => setMode(v as "videos" | "chunks" | "semantic")}>
            <TabsList>
              <TabsTrigger value="videos">
                <Video class="h-4 w-4 mr-1" />
                Videos
              </TabsTrigger>
              <TabsTrigger value="chunks">
                <FileText class="h-4 w-4 mr-1" />
                Transcript Chunks
              </TabsTrigger>
              <TabsTrigger value="semantic">
                <Sparkles class="h-4 w-4 mr-1" />
                Semantic
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <Show when={mode() === "semantic"}>
            <div class="rounded-md border border-primary/20 bg-primary/5 p-3 text-sm">
              <div class="flex items-center gap-2">
                <Zap class="h-4 w-4 text-primary" />
                <span class="font-medium">AI-Powered Semantic Search</span>
              </div>
              <p class="text-muted-foreground mt-1">
                Uses AI embeddings to find conceptually similar content, even if keywords don't match exactly.
              </p>
            </div>
          </Show>

          <Show when={showFilters()}>
            <Card>
              <CardContent class="p-4">
                <div class="grid gap-4 md:grid-cols-3">
                  <div>
                    <label class="text-sm font-medium mb-2 block">Channels</label>
                    <ScrollArea class="h-32 rounded border p-2">
                      <For each={channels() || []}>
                        {(channel) => (
                          <label class="flex items-center gap-2 py-1 cursor-pointer hover:bg-accent rounded px-1">
                            <input type="checkbox" class="rounded" />
                            <span class="text-sm">{channel.name}</span>
                          </label>
                        )}
                      </For>
                    </ScrollArea>
                  </div>
                  <div>
                    <label class="text-sm font-medium mb-2 block">Topics</label>
                    <ScrollArea class="h-32 rounded border p-2">
                      <For each={topics() || []}>
                        {(topic) => (
                          <label class="flex items-center gap-2 py-1 cursor-pointer hover:bg-accent rounded px-1">
                            <input type="checkbox" class="rounded" />
                            <span class="text-sm">{topic.name}</span>
                          </label>
                        )}
                      </For>
                    </ScrollArea>
                  </div>
                  <div>
                    <label class="text-sm font-medium mb-2 block">Date Range</label>
                    <div class="space-y-2">
                      <Input type="date" placeholder="From" />
                      <Input type="date" placeholder="To" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Show>

          <Suspense fallback={<SearchSkeleton />}>
            <Show when={activeQuery()}>
              <Show when={searchResults()} fallback={<SearchSkeleton />}>
                {(items) => (
                  <Show
                    when={items().length > 0}
                    fallback={
                      <EmptyState
                        icon={<Search class="h-12 w-12" />}
                        title="No results found"
                        description={`No ${mode()} match "${activeQuery()}". Try different keywords.`}
                      />
                    }
                  >
                    <div class="space-y-3">
                      <p class="text-sm text-muted-foreground">
                        {items().length} results for "{activeQuery()}"
                      </p>
                      <Show when={mode() === "videos"}>
                        <For each={videoResults() ?? []}>
                          {(result) => (
                            <A href={`/video/${result.id}`}>
                              <Card class="hover:bg-accent/50 transition-colors">
                                <CardContent class="p-4">
                                  <div class="flex gap-4">
                                    <div class="relative flex-shrink-0">
                                      <img
                                        src={result.thumbnailUrl}
                                        alt=""
                                        class="h-20 w-36 rounded object-cover"
                                      />
                                      <div class="absolute bottom-1 right-1 rounded bg-black/80 px-1 py-0.5 text-xs text-white">
                                        {formatDuration(result.duration)}
                                      </div>
                                    </div>
                                    <div class="flex-1 min-w-0">
                                      <h3 class="font-medium line-clamp-2">{result.title}</h3>
                                      <p class="text-sm text-muted-foreground mt-1">
                                        {result.channel?.name} • {formatRelativeTime(result.publishedAt)}
                                      </p>
                                      <Show when={result.summary}>
                                        <p class="text-sm text-muted-foreground mt-2 line-clamp-2">
                                          {result.summary}
                                        </p>
                                      </Show>
                                      <div class="flex gap-2 mt-2">
                                        <For each={result.topicTags?.slice(0, 3) || []}>
                                          {(tag) => <Badge variant="secondary">{tag}</Badge>}
                                        </For>
                                      </div>
                                    </div>
                                    <div class="flex flex-col gap-1">
                                      <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        title="Add to dossier"
                                        aria-label="Add to dossier"
                                      >
                                        <FolderPlus class="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        title="Add to analysis"
                                        aria-label="Add to analysis"
                                      >
                                        <Plus class="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            </A>
                          )}
                        </For>
                      </Show>
                      <Show when={mode() === "chunks"}>
                        <For each={chunkResults() ?? []}>
                          {(result) => (
                            <Card class="hover:bg-accent/50 transition-colors">
                              <CardContent class="p-4">
                                <div class="flex items-start gap-3">
                                  <button class="text-xs text-muted-foreground hover:text-primary font-mono flex-shrink-0">
                                    {formatDuration(result.startTime)}
                                  </button>
                                  <div class="flex-1">
                                    <p class="text-sm">{result.text}</p>
                                    <Show when={result.speaker}>
                                      <p class="text-xs text-muted-foreground mt-1">
                                        Speaker: {result.speaker}
                                      </p>
                                    </Show>
                                  </div>
                                  <div class="flex gap-1">
                                    <Button
                                      variant="ghost"
                                      size="icon-sm"
                                      title="Add to dossier"
                                      aria-label="Add to dossier"
                                    >
                                      <FolderPlus class="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          )}
                        </For>
                      </Show>
                      <Show when={mode() === "semantic"}>
                        <For each={semanticResults() ?? []}>
                          {(result) => (
                            <A href={`/video/${result.videoId}`}>
                              <Card class="hover:bg-accent/50 transition-colors">
                                <CardContent class="p-4">
                                  <div class="flex gap-4">
                                    <div class="flex-shrink-0">
                                      <div class="h-16 w-16 rounded-md bg-primary/10 flex items-center justify-center">
                                        <Sparkles class="h-6 w-6 text-primary" />
                                      </div>
                                    </div>
                                    <div class="flex-1 min-w-0">
                                      <div class="flex items-center gap-2">
                                        <h3 class="font-medium line-clamp-1">{result.videoTitle}</h3>
                                        <Badge variant="outline" class="text-xs">
                                          {Math.round(result.score * 100)}% match
                                        </Badge>
                                      </div>
                                      <p class="text-sm text-muted-foreground">
                                        {result.channelName} • {formatDuration(result.startTime)}
                                      </p>
                                      <p class="text-sm text-muted-foreground mt-2 line-clamp-2">
                                        {result.text}
                                      </p>
                                    </div>
                                    <div class="flex flex-col gap-1">
                                      <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        title="Add to dossier"
                                        aria-label="Add to dossier"
                                      >
                                        <FolderPlus class="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        title="Add to analysis"
                                        aria-label="Add to analysis"
                                      >
                                        <Plus class="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            </A>
                          )}
                        </For>
                      </Show>
                    </div>
                  </Show>
                )}
              </Show>
            </Show>
          </Suspense>

          <Show when={!activeQuery()}>
            <div class="mt-8 text-center">
              <Search class="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h3 class="text-lg font-medium">Start searching</h3>
              <p class="text-muted-foreground mt-1">
                Enter keywords to search across videos and transcripts
              </p>
            </div>
          </Show>
        </div>
        </div>
      </div>
    </AppShell>
  );
}

function SearchSkeleton() {
  return (
    <div class="space-y-3">
      <For each={[1, 2, 3]}>
        {() => (
          <Card>
            <CardContent class="p-4">
              <div class="flex gap-4">
                <Skeleton class="h-20 w-36 rounded" />
                <div class="flex-1 space-y-2">
                  <Skeleton class="h-5 w-3/4" />
                  <Skeleton class="h-4 w-1/2" />
                  <Skeleton class="h-4 w-full" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </For>
    </div>
  );
}

function SavedSearchesSkeleton() {
  return (
    <div class="space-y-2 p-2">
      <For each={[1, 2, 3]}>
        {() => (
          <div class="p-2">
            <Skeleton class="h-4 w-3/4 mb-1" />
            <Skeleton class="h-3 w-1/2" />
          </div>
        )}
      </For>
    </div>
  );
}
